import React, { useEffect, useState } from 'react';
import { Plus, X, MessageCircle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';
import { waLink, mergeTemplate } from '../lib/whatsapp';
import { TEMPLATES } from '../lib/content';

export default function Payouts() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => Promise.all([
    api.get('/assignments').then(r => setItems(r.data)),
    api.get('/contacts').then(r => setContacts(r.data)),
    api.get('/projects').then(r => setProjects(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/assignments/${data.id}`, data);
      else await api.post('/assignments', data);
      toast.success('Saved'); setOpen(false); setEdit(null); load();
    } catch { toast.error('Save failed'); }
  };
  const markPaid = async (a) => { await api.put(`/assignments/${a.id}`, { ...a, payout_status: 'paid', payout_date: new Date().toISOString().slice(0,10) }); load(); toast.success('Marked paid'); };

  const totalDue = items.filter(a => a.payout_status !== 'paid').reduce((s,a) => s + (a.fee||0) + (a.allowance||0), 0);

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div><div className="eyebrow">Team Rishika</div><h1 className="serif text-[28px]">Artist Payouts</h1></div>
        <button className="btn-gold" onClick={() => setOpen(true)} data-testid="payout-add-btn"><Plus size={16}/> Assign</button>
      </div>

      <div className="card-alt p-4 mb-4"><div className="eyebrow">Total pending</div><div className="serif text-[28px] mono-num" style={{color:'var(--pending)'}}>{formatINR(totalDue)}</div></div>

      <div className="space-y-2">
        {items.length === 0 && <div className="card-cream p-8 text-center text-sm text-[var(--muted)] italic">No assignments yet.</div>}
        {items.map(a => {
          const artist = contacts.find(c => c.id === a.artist_id);
          const msg = mergeTemplate(TEMPLATES.find(t=>t.key==='hired_artist_confirmation').body, {
            artist_name: a.artist_name, event_name: a.event_name, event_date: formatDate(a.event_date),
            reaching_time: '', venue: '', artist_fee: formatINR(a.fee)
          });
          return (
            <div key={a.id} className="card-cream p-4" data-testid={`payout-${a.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="serif text-[19px]">{a.artist_name} <span className="text-xs text-[var(--muted)] tracking-[0.14em] uppercase ml-2">{a.role.replace('_',' ')}</span></div>
                  <div className="text-xs text-[var(--muted)] mt-1">{a.event_name} · {formatDate(a.event_date)}</div>
                </div>
                <div className="text-right">
                  <div className="serif mono-num">{formatINR((a.fee||0)+(a.allowance||0))}</div>
                  <span className={`status-pill status-${a.payout_status==='paid'?'ok':'pending'}`}>{a.payout_status}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {a.payout_status !== 'paid' && <button className="btn-ghost text-xs" onClick={() => markPaid(a)} data-testid={`payout-paid-${a.id}`}><Check size={12}/> Mark Paid</button>}
                {artist?.phone && <a href={waLink(artist.phone, msg)} target="_blank" rel="noreferrer" className="btn-ghost text-xs"><MessageCircle size={12}/> WhatsApp</a>}
                <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard.writeText(msg); toast.success('Copied'); }}><Copy size={12}/> Copy</button>
                <button className="btn-ghost text-xs" onClick={() => { setEdit(a); setOpen(true); }}>Edit</button>
              </div>
            </div>
          );
        })}
      </div>

      {open && <AssignmentForm value={edit} contacts={contacts} projects={projects} onClose={() => { setOpen(false); setEdit(null); }} onSave={save} />}
    </div>
  );
}

function AssignmentForm({ value, contacts, projects, onClose, onSave }) {
  const [f, setF] = useState({ project_id: '', event_id: '', event_name: '', event_date: '', artist_id: '', artist_name: '', role: 'Hair Artist', fee: 0, allowance: 0, confirmed: false, payout_status: 'pending', cost_type: 'absorbed', linked_line_id: '', client_billed: 0, notes: '', ...value });
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get('/settings').then(r => setRoles(r.data.crew_roles || [])); }, []);
  const proj = projects.find(p => p.id === f.project_id);
  const artists = contacts.filter(c => c.type === 'hired_artist');
  const margin = (f.client_billed || 0) - (f.fee || 0) - (f.allowance || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-lg card-cream p-6 lg:rounded-md overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif text-[22px]">{value?.id ? 'Edit Assignment' : 'Assign Crew'}</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div><label className="eyebrow">Project</label>
            <select value={f.project_id} onChange={e => setF({ ...f, project_id: e.target.value, event_id: '' })} data-testid="payout-form-project">
              <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {proj && (
            <div><label className="eyebrow">Event</label>
              <select value={f.event_id} onChange={e => {
                const ev = proj.events.find(x => x.id === e.target.value);
                setF({ ...f, event_id: e.target.value, event_name: ev?.name || '', event_date: ev?.date || '' });
              }} data-testid="payout-form-event">
                <option value="">—</option>{(proj.events||[]).map(ev => <option key={ev.id} value={ev.id}>{ev.name} · {ev.date}</option>)}
              </select>
            </div>
          )}
          <div><label className="eyebrow">Person</label>
            <select value={f.artist_id} onChange={e => {
              const c = artists.find(x => x.id === e.target.value);
              setF({ ...f, artist_id: e.target.value, artist_name: c?.name || '', role: c?.speciality || f.role, fee: c?.standard_rate || f.fee });
            }} data-testid="payout-form-artist">
              <option value="">—</option>{artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <div className="text-[10px] text-[var(--muted)] mt-1">Add new people under Contacts → type "hired_artist".</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Role</label>
              <select value={f.role} onChange={e => setF({ ...f, role: e.target.value })}>
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label className="eyebrow">Payout status</label>
              <select value={f.payout_status} onChange={e => setF({ ...f, payout_status: e.target.value })}>
                <option value="pending">Pending</option><option value="due">Due</option><option value="paid">Paid</option>
              </select>
            </div>
          </div>

          {/* Cost type — critical distinction */}
          <div className="card-alt p-3">
            <label className="eyebrow block mb-2">Cost type</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setF({ ...f, cost_type: 'absorbed' })}
                className="flex-1 px-3 py-2 text-xs rounded"
                style={{background: f.cost_type==='absorbed'?'var(--charcoal)':'transparent', color: f.cost_type==='absorbed'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}
                data-testid="cost-type-absorbed">Absorbed (my expense)</button>
              <button type="button" onClick={() => setF({ ...f, cost_type: 'recharged' })}
                className="flex-1 px-3 py-2 text-xs rounded"
                style={{background: f.cost_type==='recharged'?'var(--charcoal)':'transparent', color: f.cost_type==='recharged'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}
                data-testid="cost-type-recharged">Recharged to client</button>
            </div>
            <div className="text-[10px] text-[var(--muted)] mt-2">
              {f.cost_type==='absorbed' ? 'Never appears on client documents. Reduces your profit.' : 'A line item on the invoice. Client rate is independent of what you pay.'}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Fee I pay (₹)</label><input type="number" value={f.fee} onChange={e => setF({ ...f, fee: parseFloat(e.target.value||0) })} data-testid="payout-fee"/></div>
            <div><label className="eyebrow">Allowance (₹)</label><input type="number" value={f.allowance} onChange={e => setF({ ...f, allowance: parseFloat(e.target.value||0) })}/></div>
            {f.cost_type === 'recharged' && (
              <>
                <div><label className="eyebrow">Client billed (₹)</label><input type="number" value={f.client_billed} onChange={e => setF({ ...f, client_billed: parseFloat(e.target.value||0) })} data-testid="payout-client-billed"/></div>
                <div><label className="eyebrow">Margin</label><div className="serif mono-num text-[18px] pt-2" style={{color: margin>=0?'var(--ok)':'var(--overdue)'}}>{margin >= 0 ? '+' : ''}₹{margin}</div></div>
              </>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={f.confirmed} onChange={e => setF({ ...f, confirmed: e.target.checked })} data-testid="payout-form-confirmed"/> Confirmed</label>
          <div><label className="eyebrow">Notes</label><textarea rows={2} value={f.notes||''} onChange={e => setF({ ...f, notes: e.target.value })}/></div>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="payout-form-save">Save</button>
        </div>
      </div>
    </div>
  );
}
